import { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { EtainablSpinner } from '@/components/etainabl-spinner'
import { useKbArticles, useKbCollections } from '@/queries/kb'
import { useIsAdmin } from '@/lib/auth'
import { track } from '@/lib/analytics'
import { KbRestrictedBadge } from './KbRestrictedBadge'
import { KbArticleList } from './KbArticleList'

const filterSelectClass =
  'h-[var(--etn-control-height-input)] shrink-0 rounded-etn-pill border border-etn-border-input bg-etn-surface px-4 text-etn-control text-etn-text-cell'

// The API's default page size, which the search leaves in place.
const PAGE_SIZE = 25

function articleCountLabel(n: number) {
  return `${n} article${n === 1 ? '' : 's'}`
}

export function KbSearchView() {
  const [q, setQ] = useState('')
  const [collection, setCollection] = useState('')
  const [skip, setSkip] = useState(0)
  const isAdmin = useIsAdmin()
  const collections = useKbCollections()
  const searching = Boolean(q.trim() || collection)
  const { data, isLoading } = useKbArticles(
    { ...(q.trim() ? { q: q.trim() } : {}), ...(collection ? { collection } : {}), ...(skip ? { skip } : {}) },
    { enabled: searching },
  )

  useEffect(() => {
    const trimmed = q.trim()
    if (trimmed.length < 3) return
    const handle = setTimeout(() => {
      track('kb_search_performed', {
        query_length: trimmed.length,
        result_count: data?.data.length ?? 0
      })
    }, 800)
    return () => clearTimeout(handle)
  }, [q, data?.data.length])

  const collectionName = collections.data?.find(c => c.slug === collection)?.name

  return (
    <div className="container mx-auto max-w-3xl p-6 space-y-6">
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-semibold flex-1">Knowledgebase</h1>
        {isAdmin && (
          <Link to="/admin/kb" className="text-sm text-muted-foreground hover:text-foreground underline">
            Manage articles
          </Link>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search articles…"
            aria-label="Search articles"
            className="pl-9"
            value={q}
            onChange={e => {
              setQ(e.target.value)
              setSkip(0)
            }}
          />
        </div>
        {(collections.data?.length ?? 0) > 1 && (
          <select aria-label="Collection" className={filterSelectClass} value={collection} onChange={e => {
              setCollection(e.target.value)
              setSkip(0)
            }}>
            <option value="">All collections</option>
            {collections.data?.map(c => (
              <option key={c._id} value={c.slug}>{c.name}</option>
            ))}
          </select>
        )}
      </div>

      {searching ? (
        <KbArticleList
          articles={data?.data}
          isLoading={isLoading}
          isAdmin={isAdmin}
          showCollection={!collection}
          page={data ? { total: data.total, skip, limit: PAGE_SIZE, onSkip: setSkip } : undefined}
          emptyText={q.trim() ? `No articles match "${q.trim()}"${collectionName ? ` in ${collectionName}` : ''}.` : 'No articles in this collection yet.'}
        />
      ) : collections.isLoading ? (
        <div className="py-8 flex justify-center">
          <EtainablSpinner size="sm" />
        </div>
      ) : (
        <ul aria-label="Collections" className="grid gap-3 sm:grid-cols-2">
          {collections.data?.map(c => (
            <li key={c._id}>
              <Link
                to={`/kb/c/${c.slug}` as never}
                className="flex h-full flex-col gap-1 rounded-etn-card border border-etn-border-hairline bg-etn-surface p-4 shadow-etn-whisper hover:bg-muted transition-colors"
              >
                <span className="flex items-center gap-2 font-medium">
                  {c.name}
                  {isAdmin && <KbRestrictedBadge article={c} />}
                </span>
                {c.description && <span className="text-sm text-muted-foreground">{c.description}</span>}
                <span className="mt-auto pt-2 text-xs text-muted-foreground">{articleCountLabel(c.articleCount)}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
